/* eslint-disable sonarjs/no-duplicate-string */
import type { TWidget } from 'widgets/Widget';
import type { TFitGuideFormInstance } from './FitGuideForm';
import type { TFitGuideResultInstance } from './FitGuideResult';
import type { TRefElementInstance } from 'widgets/toolbox/RefElement';
import type { ISizeChart, TFitGuideSuggestionInfo } from './FitGuide.types';
import { getContentByUrl } from 'widgets/toolbox/ajax';
import { getData, log } from 'widgets/toolbox/util';

/**
 * @description FitGuideMgr implementation
 * @param Widget Base widget for extending
 * @returns Fit Guide Manager class
 */
function FitGuideMgrClassCreator(Widget: TWidget) {
    /**
     * @category widgets
     * @subcategory product/fitguide
     * @class FitGuideMgr
     * @augments Widget
     * @classdesc FitGuideMgr component manages the fit guide functionality for products.
     * Widget manages next actions:
     * 1. Initialize size chart data
     * 2. Handle view navigation between different fit guide screens
     * 3. Manage form and result interactions
     * 4. Load dynamic content for info views
     * 5. Process fit guide calculations and display results
     * @property {string} data-widget - Widget name `fitGuideMgr`
     * @property {object} data-size-chart - Size chart configuration data
     * @property {string} data-product-id - Product identifier for fit guide calculations
     * @property {string} data-text-back-calculator - Text for back to calculator button
     *
     * @example <caption>Example of FitGuideMgr widget markup</caption>
     * <div
     *     data-widget="fitGuideMgr"
     *     data-size-chart="${JSON.stringify(sizeChart)}"
     *     data-product-id="${productId}"
     *     data-text-back-calculator="${Resource.msg('button.back.calculator', 'fitguide', null)}"
     * >
     *     <div data-ref="default-view">
     *         <!-- Default fit guide view content -->
     *     </div>
     * </div>
     */
    class FitGuideMgr extends Widget {
        /**
         * @description Size chart data containing measurements for different age groups and genders
         * @type {ISizeChart}
         */
        sizeChart: ISizeChart = {
            kids: [],
            adults: {
                men: [],
                women: []
            }
        };

        /**
         * @description Returns widget preferences with default values
         * @returns Widget preferences object
         */
        prefs() {
            return {
                sizeChart: {} as ISizeChart,
                productId: '',
                itemId: '',
                itemVariant: '',
                viewMode: 'default' as 'default' | 'cart' | 'content',
                ...super.prefs()
            };
        }

        /**
         * @description Widget initialization - sets up size chart and populates form options
         */
        init(): void {
            if (!this.prefs().sizeChart) {
                return;
            }

            this.sizeChart = this.prefs().sizeChart;

            this.getById('fitGuideForm', (el: TFitGuideFormInstance) => {
                el.populateSelectOptions(this.sizeChart, this.prefs().productId, this.prefs().itemId, this.prefs().itemVariant);
            });
        }

        /**
         * @description Opens the default fit guide view and hides the specified target view
         * @param el - Element that triggered the action
         */
        openDefaultView(el) {
            this.ref('default-view').show();
            this.ref(el.data('target')).hide();
            this.eventBus().emit('update.drawer.title', { default: true });
        }

        /**
         * @description Opens a specific view by reference name and hides the default view
         * @param refName - Reference name of the view to open
         */
        openSelectedView(refName: string) {
            this.ref('default-view').hide();
            this.ref(refName).show();

            this.pushSelectedViewAnalytics(refName);
        }

        /**
         * @description Pushes analytics event for the selected view
         * @param refName - Reference name of the view to track
         */
        pushSelectedViewAnalytics(refName: string) {
            let eventName;

            switch (refName) {
                case 'how-to-measure':
                    eventName = 'click_how_to_measure';
                    break;
                case 'size-chart':
                    eventName = 'click_size_info';
                    break;
                case 'width-information':
                    eventName = 'click_width_info';
                    break;
                default:
                    break;
            }

            if (eventName) {
                this.eventBus().emit('gtm.event', {
                    event_name: eventName,
                    [eventName]: {
                        feature: 'fit_guide',
                        item_id: this.prefs().itemId || this.prefs().productId,
                        item_variant: this.prefs().itemVariant?.toString()
                    }
                });
            }
        }

        /**
         * @description Recalculates the fit guide by resetting form and hiding results
         * Re-populates form options and shows the form again
         */
        recalculateFitGuide() {
            this.getById('suggestion-result', (el: TFitGuideResultInstance) => el.ref('self').hide());
            this.getById('fitGuideForm', (formEl: TFitGuideFormInstance) => {
                formEl.populateSelectOptions(this.sizeChart, this.prefs().productId, this.prefs().itemId, this.prefs().itemVariant);

                formEl.ref('self').show();
                formEl.recalculated = true;
            });
        }

        /**
         * @description Handles info link clicks to load dynamic content views
         * Fetches content from the link URL and creates a new view with back navigation
         * @param el - The clicked link element
         * @returns Promise that resolves when content is loaded
         */
        async handleInfoLinkClick(el: TRefElementInstance) {
            const href = el.attr('href');
            const target = (el.data('target-view') as string) || 'self';
            const targetRef = this.ref(target);
            const dialogTitle = el.data('dialog-title') || false;

            if (!href || !targetRef) {
                return;
            }

            this.eventBus().emit('gtm.event', {
                event_name: 'click_size_info',
                click_size_info: {
                    feature: 'fit_guide',
                    item_id: this.prefs().itemId || this.prefs().productId,
                    item_variant: this.prefs().itemVariant?.toString()
                }
            });

            if (this.prefs().viewMode === 'content') {
                this.eventBus().emit('drawershow', {
                    url: href,
                    headerText: dialogTitle,
                    bodyClass: 'm-has_dialog',
                    wrapperClasses: 'fit-guide-view m-fit_guide'
                });

                this.pushSelectedViewAnalytics(target);

                return;
            }

            this.eventBus().emit('update.drawer.title', { title: dialogTitle });

            if (targetRef?.get()) {
                this.openSelectedView(target);

                return;
            }

            const url = new URL(href, window.location.origin);

            this.getById('fitGuideForm', (formEl: TFitGuideFormInstance) => {
                url.searchParams.set('mainSizing', formEl.getMainSize());
            });

            getContentByUrl(url.toString())
                .then(res => {
                    const wrapper = document.createElement('div');
                    const backButton = document.createElement('button');

                    backButton.className = 'b-continue_shopping m-fit_guide';
                    backButton.setAttribute('data-event-click', 'openDefaultView');
                    backButton.setAttribute('data-target', target);
                    backButton.innerHTML = `
                    <svg
                        width="15"
                        height="15"
                        focusable="false"
                        aria-hidden="true"
                        class="b-continue_shopping-icon"
                    >
                        <use href="#arrow-down"></use>
                    </svg>
                    <span class="b-continue_shopping-text">
                        ${this.ref('self').data('text-back-calculator')}
                    </span>
                `;

                    wrapper.className = 'fit-guide-view';
                    wrapper.setAttribute('data-ref', target);
                    wrapper.insertAdjacentElement('afterbegin', backButton);
                    wrapper.insertAdjacentHTML('beforeend', res);

                    this.ref('default-view')?.get()?.insertAdjacentElement('afterend', wrapper);

                    this.openSelectedView(target);
                })
                .catch(err => {
                    if (!PRODUCTION) {
                        log.error('Failed to getContentByUrl info view:' + err, this);
                    }
                });
        }

        /**
         * @description Processes fit guide suggestion results and displays them
         * Hides the form and shows the result view with calculated size data
         * @param form - The form instance that generated the suggestion
         * @param data - The calculated fit guide suggestion data
         */
        onFitGuideSuggestionResult(form: TFitGuideFormInstance, data: TFitGuideSuggestionInfo) {
            this.getById('suggestion-result', (fitGuideResult: TFitGuideResultInstance) => {
                form.ref('self').hide();

                fitGuideResult.populateFitGuideSuggestion(data, this.prefs().viewMode);

                fitGuideResult.populateSuggestedSize(data);

                const productSuggestion = fitGuideResult.ref('product-suggestion').get();
                const addToCartFGButton = productSuggestion?.querySelector('[data-addtocart-fg-button]');

                if (addToCartFGButton) {
                    const analytics = getData(addToCartFGButton.getAttribute('data-analytics'));

                    // preserve item variant for analytics tracking
                    form.itemVariant = analytics?.item_variant;
                }
            });
        }
    }

    return FitGuideMgr;
}

export type TFitGuideMgr = ReturnType<typeof FitGuideMgrClassCreator>;

export type TFitGuideMgrInstance = InstanceType<TFitGuideMgr>;

export default FitGuideMgrClassCreator;
